import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Icon,
    Wrap,
    WrapItem,
    Input,
    InputGroup,
    InputLeftElement,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
} from '@chakra-ui/react';
import {
    FaSync,
    FaUsers,
    FaUser,
    FaSearch,
    FaChartBar,
    FaExclamationTriangle,
    FaLightbulb,
    FaBrain,
    FaBalanceScale,
    FaChartPie,
    FaUserCheck,
    FaDownload,
} from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    AreaChart,
    Area,
    ScatterChart,
    Scatter,
    ZAxis,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMdashboardPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [downloadingPng, setDownloadingPng] = useState(false);
    const toast = useToast();

    const isMobile = useBreakpointValue({ base: true, md: false });
    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';
    const yellowColor = isDarkTheme ? '#F6E05E' : '#D69E2E';
    const infoColor = isDarkTheme ? '#63B3ED' : '#3182CE';

    // Info card backgrounds - improved transparency for dark theme
    const successBg = isDarkTheme ? 'rgba(104, 211, 145, 0.15)' : '#D1FAE5';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FEE2E2';
    const warningBg = isDarkTheme ? 'rgba(246, 173, 85, 0.15)' : '#FED7AA';
    const infoBg = isDarkTheme ? 'rgba(99, 179, 237, 0.15)' : '#BEE3F8';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Chart axis color
    const axisColor = isDarkTheme ? bodyTextColor : '#4A5568';

    // Chart colors - brighter for dark theme
    const chartPrimaryColor = isDarkTheme ? accentColor : primaryColor;

    // Color palettes for charts
    const PORTFOLIO_COLORS = isDarkTheme
        ? [accentColor, successColor, warningColor]
        : [primaryColor, successColor, warningColor];

    const BIAS_COLORS = isDarkTheme
        ? [accentColor, '#B794F4', warningColor, errorColor, yellowColor]
        : [primaryColor, '#805AD5', warningColor, errorColor, yellowColor];

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/rm-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDashboardData(res.data);
        } catch (error) {
            console.error('Dashboard fetch error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load dashboard data.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
            setDashboardData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleExportPng = async () => {
        try {
            setDownloadingPng(true);
            const response = await axios.get(`${API_BASE_URL}/content/export-png/rm`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important for file download
            });

            // Create a download link and trigger it
            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_rm_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Success',
                description: 'Dashboard exported as PNG successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            console.error('Export PNG error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to export dashboard.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDownloadingPng(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line
    }, []);

    // Calculate statistics from metrics
    const getStatistics = () => {
        if (!dashboardData?.metrics) {
            return {
                totalCustomers: 0,
                clientBookSize: 0,
                averageLoginGap: 0,
                clientsWithBiases: 0,
                averageQuizScore: 0,
                biasMitigationCount: 0,
                averageEngagement: 0,
                averageMismatch: 0,
            };
        }

        const metrics = dashboardData.metrics;
        return {
            totalCustomers: dashboardData.summary?.total_customers || 0,
            clientBookSize: metrics.client_book_size || 0,
            averageLoginGap: metrics.average_login_gap_days || 0,
            clientsWithBiases: metrics.clients_with_active_biases || 0,
            averageQuizScore: metrics.average_knowledge_quiz_scores || 0,
            biasMitigationCount: metrics.bias_mitigation_opportunity_count || 0,
            averageEngagement: metrics.client_engagement_score?.average_engagement_score || 0,
            averageMismatch: metrics.risk_alignment_delta?.average_mismatch || 0,
        };
    };

    // Prepare portfolio mix data for pie chart
    const getPortfolioMixData = () => {
        if (!dashboardData?.metrics?.portfolio_mix_distribution) return [];

        const mix = dashboardData.metrics.portfolio_mix_distribution;
        return [
            { name: 'Cash', value: mix.cash_pct, color: PORTFOLIO_COLORS[0] },
            { name: 'ETFs', value: mix.etf_pct, color: PORTFOLIO_COLORS[1] },
            { name: 'Single Stocks', value: mix.single_stock_pct, color: PORTFOLIO_COLORS[2] },
        ];
    };

    // Prepare risk mismatch data
    const getRiskMismatchData = () => {
        if (!dashboardData?.metrics?.risk_alignment_delta) return [];

        const risk = dashboardData.metrics.risk_alignment_delta;
        return [
            { name: 'High', value: risk.high_mismatch_count, color: errorColor },
            { name: 'Medium', value: risk.medium_mismatch_count, color: warningColor },
            { name: 'Low', value: risk.low_mismatch_count, color: successColor },
        ];
    };

    // Prepare top biases data
    const getTopBiasesData = () => {
        if (!dashboardData?.top_biases) return [];

        return dashboardData.top_biases.map((bias, index) => ({
            name: bias.bias_name,
            value: bias.mean_score,
            affected: bias.affected_customers,
            color: BIAS_COLORS[index % BIAS_COLORS.length],
        }));
    };

    // Prepare age distribution data
    const getAgeDistributionData = () => {
        if (!dashboardData?.age_distribution) return [];

        return dashboardData.age_distribution.map(item => ({
            name: item.bucket,
            value: item.count,
            percentage: item.percentage,
        }));
    };

    // Prepare next best contact data for scatter plot
    const getNextBestContactData = () => {
        if (!dashboardData?.metrics?.next_best_contact_score) return [];

        return dashboardData.metrics.next_best_contact_score.map(customer => ({
            id: customer.customer_id,
            priority: customer.priority_score,
            riskMismatch: customer.risk_mismatch?.mismatch_score || 0,
            urgency: customer.urgency_level,
            recommendedAction: customer.recommended_action,
            topBias: customer.top_biases?.[0]?.bias_name || 'Unknown',
            biasScore: customer.top_biases?.[0]?.score || 0,
        }));
    };

    // Filter individual customers based on search
    const getFilteredCustomers = () => {
        if (!dashboardData?.individual_customer_analysis) return [];
        if (!searchTerm) return dashboardData.individual_customer_analysis;

        return dashboardData.individual_customer_analysis.filter(
            customer =>
                customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.country_iso.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.recommended_action.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const getUrgencyBadgeColor = (urgencyLevel) => {
        switch (urgencyLevel?.toUpperCase()) {
            case 'CRITICAL': return 'red';
            case 'HIGH': return 'orange';
            case 'MEDIUM': return 'yellow';
            case 'LOW': return 'green';
            default: return 'gray';
        }
    };

    const getPriorityColor = (score) => {
        if (score >= 8) return errorColor;
        if (score >= 6) return warningColor;
        if (score >= 4) return yellowColor;
        return successColor;
    };

    const formatNumber = (num) => {
        return typeof num === 'number' ? num.toFixed(1) : '0';
    };

    const stats = getStatistics();
    const portfolioMixData = getPortfolioMixData();
    const riskMismatchData = getRiskMismatchData();
    const topBiasesData = getTopBiasesData();
    const ageDistributionData = getAgeDistributionData();
    const nextBestContactData = getNextBestContactData();
    const filteredCustomers = getFilteredCustomers();

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
                        <Box flex="1">
                            <Heading
                                size={{ base: 'lg', md: 'xl' }}
                                color={headingTextColor}
                                mb={2}
                                fontWeight="bold"
                            >
                                <HStack spacing={3}>
                                    <Icon as={FaChartBar} boxSize={{ base: 6, md: 8 }} />
                                    <Text>Relationship Manager Dashboard</Text>
                                </HStack>
                            </Heading>
                            <Text color={bodyTextColor} fontSize="sm">
                                Portfolio insights and client prioritization
                            </Text>
                        </Box>
                        <HStack spacing={2}>
                            <Button
                                leftIcon={<FaDownload />}
                                bg={accentColor}
                                color="white"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={handleExportPng}
                                isLoading={downloadingPng}
                                _hover={{ bg: isDarkTheme ? secondaryColor : accentColor }}
                            >
                                Export PNG
                            </Button>
                            <Button
                                leftIcon={<FaSync />}
                                bg={primaryColor}
                                color="white"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={fetchDashboardData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Refresh
                            </Button>
                        </HStack>
                    </Flex>

                    {loading && (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor}>Loading dashboard...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {!loading && dashboardData && (
                        <>
                            {/* Key Metrics */}
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Client Book Size
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                {stats.clientBookSize}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Total clients
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaBrain} color={warningColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Bias Opportunities
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={warningColor} fontWeight="bold">
                                                {stats.biasMitigationCount}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                For mitigation
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaBalanceScale} color={infoColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Risk Mismatch
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={infoColor} fontWeight="bold">
                                                {formatNumber(stats.averageMismatch)}%
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Average delta
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaUserCheck} color={successColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Engagement Score
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={successColor} fontWeight="bold">
                                                {formatNumber(stats.averageEngagement)}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Average
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Risk and Bias Summary */}
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                <Card bg={errorBg} borderRadius="lg" boxShadow="sm" border="1px" borderColor={errorColor}>
                                    <CardBody p={{ base: 3, md: 4 }}>
                                        <Stat size={statSize}>
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>High Risk Mismatch</StatLabel>
                                            <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color={errorColor} fontWeight="bold">
                                                {dashboardData.metrics?.risk_alignment_delta?.high_mismatch_count || 0}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize="xs">
                                                {dashboardData.metrics?.risk_alignment_delta?.high_mismatch_pct || 0}% of clients
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card bg={warningBg} borderRadius="lg" boxShadow="sm" border="1px" borderColor={warningColor}>
                                    <CardBody p={{ base: 3, md: 4 }}>
                                        <Stat size={statSize}>
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>Clients with Biases</StatLabel>
                                            <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color={warningColor} fontWeight="bold">
                                                {stats.clientsWithBiases}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize="xs">
                                                Active behavioral biases
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card bg={infoBg} borderRadius="lg" boxShadow="sm" border="1px" borderColor={infoColor}>
                                    <CardBody p={{ base: 3, md: 4 }}>
                                        <Stat size={statSize}>
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>Avg Quiz Score</StatLabel>
                                            <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color={infoColor} fontWeight="bold">
                                                {formatNumber(stats.averageQuizScore)}%
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize="xs">
                                                Financial knowledge
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card bg={successBg} borderRadius="lg" boxShadow="sm" border="1px" borderColor={successColor}>
                                    <CardBody p={{ base: 3, md: 4 }}>
                                        <Stat size={statSize}>
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>Avg Login Gap</StatLabel>
                                            <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color={successColor} fontWeight="bold">
                                                {stats.averageLoginGap} days
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize="xs">
                                                Between logins
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Charts Row 1: Portfolio Mix and Risk Distribution */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Portfolio Mix Chart */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaChartPie} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Portfolio Mix Distribution
                                            </Heading>
                                        </HStack>
                                        {portfolioMixData.length > 0 ? (
                                            <Box>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <PieChart>
                                                        <Pie
                                                            data={portfolioMixData}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                                                            outerRadius={80}
                                                            dataKey="value"
                                                        >
                                                            {portfolioMixData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip
                                                            contentStyle={{
                                                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                            }}
                                                            formatter={(value) => [`${value.toFixed(1)}%`, 'Percentage']}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <Wrap spacing={2} mt={3} justify="center">
                                                    {portfolioMixData.map((item, index) => (
                                                        <WrapItem key={index}>
                                                            <Badge
                                                                px={3}
                                                                py={1}
                                                                borderRadius="full"
                                                                bg={item.color}
                                                                color="white"
                                                                fontSize="xs"
                                                            >
                                                                {item.name}: {item.value.toFixed(1)}%
                                                            </Badge>
                                                        </WrapItem>
                                                    ))}
                                                </Wrap>
                                            </Box>
                                        ) : (
                                            <Flex justify="center" align="center" h="250px">
                                                <Text color={mutedTextColor}>No portfolio data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                {/* Risk Mismatch Distribution */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaExclamationTriangle} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Risk Mismatch Distribution
                                            </Heading>
                                        </HStack>
                                        {riskMismatchData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={280}>
                                                <BarChart data={riskMismatchData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis
                                                        dataKey="name"
                                                        stroke={axisColor}
                                                        angle={0}
                                                        textAnchor="middle"
                                                        height={60}
                                                        tick={{ fontSize: isMobile ? 10 : 12, fill: axisColor }}
                                                    />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                        formatter={(value) => [`${value} clients`, 'Count']}
                                                    />
                                                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                        {riskMismatchData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No risk data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Charts Row 2: Top Biases and Age Distribution */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Top Biases Chart */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Top Behavioral Biases
                                            </Heading>
                                        </HStack>
                                        {topBiasesData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={280}>
                                                <BarChart data={topBiasesData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis
                                                        dataKey="name"
                                                        stroke={axisColor}
                                                        angle={-20}
                                                        textAnchor="end"
                                                        height={60}
                                                        tick={{ fontSize: isMobile ? 9 : 11, fill: axisColor }}
                                                    />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} domain={[0, 100]} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                        formatter={(value, name) => {
                                                            if (name === 'Score') return [`${value.toFixed(1)}%`, 'Mean Score'];
                                                            return [value, 'Affected Clients'];
                                                        }}
                                                    />
                                                    <Bar dataKey="value" radius={[8, 8, 0, 0]} name="Score">
                                                        {topBiasesData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No bias data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                {/* Age Distribution Chart */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaUser} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Age Distribution
                                            </Heading>
                                        </HStack>
                                        {ageDistributionData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={280}>
                                                <AreaChart data={ageDistributionData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis
                                                        dataKey="name"
                                                        stroke={axisColor}
                                                        tick={{ fontSize: isMobile ? 10 : 12, fill: axisColor }}
                                                    />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                        formatter={(value) => [`${value} clients`, 'Count']}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke={chartPrimaryColor}
                                                        fill={chartPrimaryColor}
                                                        fillOpacity={0.6}
                                                        strokeWidth={2}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No age data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Next Best Contact Scatter Plot */}
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaLightbulb} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Next Best Contact - Priority Analysis
                                        </Heading>
                                    </HStack>
                                    {nextBestContactData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={350}>
                                            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                <XAxis
                                                    type="number"
                                                    dataKey="priority"
                                                    name="Priority Score"
                                                    stroke={axisColor}
                                                    tick={{ fill: axisColor }}
                                                    domain={[5, 10]}
                                                />
                                                <YAxis
                                                    type="number"
                                                    dataKey="riskMismatch"
                                                    name="Risk Mismatch %"
                                                    stroke={axisColor}
                                                    tick={{ fill: axisColor }}
                                                    domain={[0, 100]}
                                                />
                                                <ZAxis type="number" dataKey="biasScore" range={[50, 400]} name="Bias Score" />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                        border: `1px solid ${borderColor}`,
                                                        borderRadius: '8px',
                                                        color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                    }}
                                                    formatter={(value, name, props) => {
                                                        if (name === 'Priority Score') return [`${value.toFixed(2)}`, name];
                                                        if (name === 'Risk Mismatch %') return [`${value}%`, name];
                                                        if (name === 'Bias Score') return [`${value}%`, name];
                                                        return [value, name];
                                                    }}
                                                    labelFormatter={(label) => `Client: ${label}`}
                                                />
                                                <Scatter
                                                    name="Clients"
                                                    data={nextBestContactData}
                                                    fill={chartPrimaryColor}
                                                    shape="circle"
                                                />
                                            </ScatterChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Flex justify="center" align="center" h="350px">
                                            <Text color={mutedTextColor}>No contact priority data available</Text>
                                        </Flex>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Customer Priority List */}
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Client Priority List ({filteredCustomers.length})
                                        </Heading>
                                    </HStack>

                                    {/* Search Bar */}
                                    <InputGroup mb={4}>
                                        <InputLeftElement pointerEvents="none">
                                            <Icon as={FaSearch} color={mutedTextColor} />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search by Client ID, Country, or Action..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            borderColor={borderColor}
                                            _focus={{
                                                borderColor: primaryColor,
                                                boxShadow: `0 0 0 1px ${primaryColor}`,
                                            }}
                                            bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'white'}
                                            color={bodyTextColor}
                                        />
                                    </InputGroup>

                                    {filteredCustomers.length > 0 ? (
                                        <TableContainer>
                                            <Table variant="simple" size={isMobile ? "sm" : "md"}>
                                                <Thead>
                                                    <Tr>
                                                        <Th color={labelTextColor}>Client ID</Th>
                                                        <Th color={labelTextColor}>Priority</Th>
                                                        <Th color={labelTextColor}>Urgency</Th>
                                                        <Th color={labelTextColor}>Top Bias</Th>
                                                        <Th color={labelTextColor}>Recommended Action</Th>
                                                        <Th color={labelTextColor}>Country</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {filteredCustomers.map((customer) => (
                                                        <Tr key={customer.customer_id} _hover={{ bg: isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'gray.50' }}>
                                                            <Td color={bodyTextColor} fontWeight="medium">
                                                                {customer.customer_id}
                                                            </Td>
                                                            <Td>
                                                                <Badge
                                                                    bg={getPriorityColor(customer.priority_score)}
                                                                    color="white"
                                                                    fontSize="xs"
                                                                    px={2}
                                                                    py={1}
                                                                    borderRadius="full"
                                                                >
                                                                    {customer.priority_score.toFixed(2)}
                                                                </Badge>
                                                            </Td>
                                                            <Td>
                                                                <Badge
                                                                    colorScheme={getUrgencyBadgeColor(customer.urgency_level)}
                                                                    fontSize="xs"
                                                                    px={2}
                                                                    py={1}
                                                                    borderRadius="full"
                                                                >
                                                                    {customer.urgency_level}
                                                                </Badge>
                                                            </Td>
                                                            <Td color={bodyTextColor}>
                                                                {customer.top_biases?.[0]?.bias_name || 'N/A'}
                                                            </Td>
                                                            <Td color={bodyTextColor} fontSize="sm">
                                                                {customer.recommended_action}
                                                            </Td>
                                                            <Td color={bodyTextColor}>
                                                                {customer.country_iso}
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Flex justify="center" align="center" py={10}>
                                            <VStack spacing={3}>
                                                <Icon as={FaSearch} boxSize={10} color={mutedTextColor} />
                                                <Text color={mutedTextColor}>
                                                    {searchTerm
                                                        ? `No clients found matching "${searchTerm}"`
                                                        : 'No client data available'}
                                                </Text>
                                            </VStack>
                                        </Flex>
                                    )}
                                </CardBody>
                            </Card>
                        </>
                    )}

                    {!loading && !dashboardData && (
                        <Card
                            bg={cardBg}
                            borderRadius="lg"
                            boxShadow="md"
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <VStack py={10} spacing={4}>
                                    <Icon as={FaChartBar} boxSize={12} color={mutedTextColor} />
                                    <Text fontSize="lg" color={mutedTextColor} fontWeight="medium">
                                        No dashboard data available
                                    </Text>
                                    <Text fontSize="sm" color={mutedTextColor}>
                                        Click refresh to load your dashboard
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMdashboardPaid;